import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { Formik, FormikHelpers } from 'formik';
import { EditPdfDetailsForm, NextStepAfterSavingPdfDetails } from './EditPdfDetailsForm';
import {
  EditPdfDetailsFormModel,
  getInitialEditPdfDetailsFormModel,
  EditPdfDetailsFormModelValidator,
} from './editPdfDetailsFormModel';
import { useApiRequest } from '../../../api/useApiRequest';
import { DataForEditPdfDetailsForm } from './dataForEditPdfDetailsForm';
import { CreateUpdatePdfDetailsCommand, mapEditPdfDetailsFormModelToCommand } from './createUpdatePdfDetailsCommand';
import { navigate } from '@reach/router';
import { GetRequest } from '../../../shared/GetRequest';
import React, { useRef } from 'react';

type EditPdfDetailsProps = {
  project: Project;
} & RouteComponentProps<{ projectId: string; designId: string; unitId: string }>;

export const EditPdfDetails = ({ project, projectId, designId, unitId }: EditPdfDetailsProps) => {
  const getPdfDetailsRequest = useApiRequest<DataForEditPdfDetailsForm>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}/designs/${designId}/units/${unitId}/pdf/details`,
  });

  const updatePdfDetailsRequest = useApiRequest<undefined, CreateUpdatePdfDetailsCommand>({
    httpMethod: 'put',
    endpointUrl: `projects/${projectId}/designs/${designId}/units/${unitId}/pdf/details/edit`,
  });

  const nextStepAfterSave = useRef<NextStepAfterSavingPdfDetails>('BackToDesignDrawings');

  const onSubmit = async (
    formModel: EditPdfDetailsFormModel,
    formikHelpers: FormikHelpers<EditPdfDetailsFormModel>,
    response: DataForEditPdfDetailsForm,
  ) => {
    const command = mapEditPdfDetailsFormModelToCommand(
      project.projectId,
      response.designId,
      response.unitId,
      formModel,
    );
    await updatePdfDetailsRequest.sendRequest({
      requestBody: command,
      onSuccess: () => {
        formikHelpers.setSubmitting(false);

        if (nextStepAfterSave.current === 'ViewPdf') {
          void navigate(projectUrls.manifoldDiagramPdf(project.projectId, response.designId, response.unitId));
        } else {
          void navigate(projectUrls.dataForDesignDrawings(project.projectId, response.designId));
        }
      },
      onFailure: () => formikHelpers.setSubmitting(false),
    });
  };

  return (
    <GetRequest getDataRequest={getPdfDetailsRequest}>
      {({ response }) => {
        const editPdfDetailsFormModelValidator = new EditPdfDetailsFormModelValidator(response.heatSource);
        const designName = response.designName;
        return (
          <>
            <BreadcrumbBar>
              <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
              <BreadcrumbSeparator />
              <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
                {project.projectReference}
              </BreadcrumbLink>
              <BreadcrumbSeparator />
              <div>{designName}</div>
              <BreadcrumbSeparator />
              <div>Configure PDF</div>
            </BreadcrumbBar>
            <ProjectHeader project={project} />
            <PageContainer>
              <AssertUserHasPermission requiredPermission="Edit">
                <Formik<EditPdfDetailsFormModel>
                  onSubmit={(values, formikHelpers) => onSubmit(values, formikHelpers, response)}
                  initialValues={getInitialEditPdfDetailsFormModel(response)}
                  validate={formModel => editPdfDetailsFormModelValidator.validate(formModel)}
                >
                  {formProps => (
                    <EditPdfDetailsForm
                      {...formProps}
                      data={response}
                      setNextStepAfterSave={nextStep => {
                        nextStepAfterSave.current = nextStep;
                      }}
                    />
                  )}
                </Formik>
              </AssertUserHasPermission>
            </PageContainer>
          </>
        );
      }}
    </GetRequest>
  );
};
